import { createSlice } from '@reduxjs/toolkit'
import { orderBy } from 'lodash'

import { getShopInventoryAPI } from '@/api/shopApi'
import { getFilteredList, getRegexSafeString, SORT_ORDER } from '@/util/misc'
import { getFromLocalStorage } from '@/util/persistance'
import { PAYMENT_TYPES, PRODUCT_TYPES } from '@/util/shop'

import {
  getCustomerId as getActiveCustomerId,
  getOrderTotalCost,
  getSelectedProducts,
  setPaymentType,
} from './shopOrderReducer'

export const NAME = 'shop-products'
// @ts-ignore
export const fetchProducts = () => async (dispatch, getState) => {
  const isPending = getIsPending(getState())
  const customerId = getActiveCustomerId(getState())
  // todo: replace with a hook when refactored
  const whiteLabel = getFromLocalStorage('org')
  const isBechtle = ['bechtle', 'arp'].includes(whiteLabel)

  if (isPending) return

  dispatch(loadPending(customerId))

  try {
    const result = await getShopInventoryAPI(customerId)

    dispatch(loadSuccess(result))

    if (!isBechtle) {
      if (!result.leasing_vendor || !getIsLeasingAvailable(getState())) {
        dispatch(setPaymentType(PAYMENT_TYPES.BUY))
      }
    }
  } catch (error) {
    dispatch(loadFailure('Could not load products'))
  }
}

const initialState = {
  isPending: false,
  isDataLoaded: false,
  error: undefined,
  customerId: undefined,

  textFilter: '',
  showOnlyInStock: false,
  mainProductTypeFilter: undefined,
  sortParam: undefined,
  sortOrder: SORT_ORDER.ASC,

  products: [],
  leasingVendor: undefined,
  frameAgreement: undefined,
  addresses: [],
}

const { actions, reducer } = createSlice({
  name: NAME,
  initialState,
  reducers: {
    loadPending: (state, { payload: customerId }) => {
      state.error = undefined
      state.isPending = true
      state.customerId = customerId
    },
    loadSuccess: (state, { payload }) => {
      const { inventory, leasing_vendor, frame_agreement, addresses } = payload
      state.isPending = false
      state.isDataLoaded = true
      state.products = inventory
      state.leasingVendor = leasing_vendor
      state.frameAgreement = frame_agreement
      state.addresses = addresses
    },
    loadFailure: (state, { payload: error }) => {
      state.isPending = false
      state.isDataLoaded = true
      state.error = error
    },
    setTextFilter: (state, { payload: textFilter }) => {
      state.textFilter = textFilter
    },
    setShowOnlyInStock: (state, { payload: showOnlyInStock }) => {
      state.showOnlyInStock = showOnlyInStock
    },
    setSortParam: (state, { payload: sortParam }) => {
      state.sortParam = sortParam
    },
    setSortOrder: (state, { payload: sortOrder }) => {
      state.sortOrder = sortOrder
    },
    toggleMainProductTypeFilter: (state, { payload: type }) => {
      state.mainProductTypeFilter = state.mainProductTypeFilter === type ? undefined : type
    },
    reset: () => ({
      ...initialState,
    }),
  },
})

export const {
  loadPending,
  loadSuccess,
  loadFailure,
  setTextFilter,
  setShowOnlyInStock,
  setSortParam,
  setSortOrder,
  toggleMainProductTypeFilter,
  reset,
} = actions

export default reducer

const getState = (state: any) => state[NAME]

export const getIsPending = (state: any) => getState(state).isPending
export const getError = (state: any) => getState(state).error
export const getIsDataLoaded = (state: any) =>
  getState(state).isDataLoaded && getCustomerId(state) === getActiveCustomerId(state)
export const getCustomerId = (state: any) => getState(state).customerId
export const getAddresses = (state: any) => getState(state).addresses

export const getTextFilter = (state: any) => getState(state).textFilter
export const getShowOnlyInStock = (state: any) => getState(state).showOnlyInStock
export const getMainProductTypeFilter = (state: any) => getState(state).mainProductTypeFilter
export const getSortParam = (state: any) => getState(state).sortParam
export const getSortOrder = (state: any) => getState(state).sortOrder

export const getProducts = (state: any) => getState(state).products
export const getMainProducts = (state: any) => getProducts(state).filter(isMainProduct)
export const getFilteredMainProducts = (state: any) =>
  getFilteredList(state, getMainProducts, textFilter, typeFilter, stockFilter)
export const getSortedFilteredMainProducts = (state: any) =>
  orderBy(getFilteredMainProducts(state), getSortParam(state), getSortOrder(state))

export const getAccessoryProducts = (state: any) => getProducts(state).filter(isAccessory)
export const getFilteredAccessoryProducts = (state: any) =>
  getFilteredList(state, getAccessoryProducts, textFilter, stockFilter, parentFilter)
export const getSortedFilteredAccessoryProducts = (state: any) =>
  orderBy(getFilteredAccessoryProducts(state), getSortParam(state), getSortOrder(state))

export const getLeasingVendor = (state: any) => getState(state).leasingVendor
export const getIsLeasingAvailable = (state: any) =>
  !!getLeasingVendor(state) && getHasFrameAgreement(state) // TODO: Remove this when EasyBank integration is ready
export const getFrameAgreement = (state: any) => getState(state).frameAgreement
export const getHasFrameAgreement = (state: any) => !!getFrameAgreement(state)
export const getFrameAgreementGrantedAmount = (state: any) =>
  getFrameAgreement(state)?.granted_amount
export const getFrameAgreementRemaining = (state: any) => getFrameAgreement(state)?.remaining
export const getIsOrderWithinCreditFrame = (state: any) => {
  const remainingAmount = getFrameAgreementRemaining(state)
  const { leasing_sum: leasingSum } = getOrderTotalCost(state)
  return remainingAmount > leasingSum
}
// @ts-ignore
export const getProductFromId = (state, productId) =>
  // @ts-ignore
  getProducts(state).find(product => product.id === productId)
// @ts-ignore
const textFilter = (state, products) => {
  const textFilter = getTextFilter(state)

  if (!textFilter) return products

  const keyWords = getRegexSafeString(textFilter).split(' ')
  // @ts-ignore
  return products.filter(product =>
    // @ts-ignore
    keyWords.every(keyWord => {
      const regExp = new RegExp(keyWord, 'i')
      return (product.name + product.product_no).match(regExp)
    })
  )
}
// @ts-ignore
const typeFilter = (state, products) => {
  const typeFilter = getMainProductTypeFilter(state)
  // @ts-ignore
  return typeFilter ? products.filter(product => typeFilter === product.type) : products
}
// @ts-ignore
const parentFilter = (state, products) => {
  const selectedProducts = getSelectedProducts(state)

  return products.filter(
    // @ts-ignore
    product =>
      product.parents.length === 0 ||
      // @ts-ignore
      selectedProducts.some(selected => product.parents.includes(selected.productId))
  )
}
// @ts-ignore
const stockFilter = (state, products) => {
  const showOnlyInStock = getShowOnlyInStock(state)

  return showOnlyInStock
    ? // @ts-ignore
      products.filter(product => !!product.in_stock && product.in_stock !== '0')
    : products
}
// @ts-ignore
const isMainProduct = product =>
  [PRODUCT_TYPES.PC, PRODUCT_TYPES.MOBILE_PHONE, PRODUCT_TYPES.TABLET].includes(product.type)
// @ts-ignore
const isAccessory = product =>
  [
    PRODUCT_TYPES.ACCESSORY,
    PRODUCT_TYPES.MONITOR,
    PRODUCT_TYPES.SOFTWARE,
    PRODUCT_TYPES.SERVICE,
    PRODUCT_TYPES.DOCKING,
  ].includes(product.type)
